import { FC, useCallback, useMemo } from 'react';

import DataTable from 'components/tables/data-table';
import { isChassisDamage, isContainerDamage, isGensetDamage } from 'types/damage/damage';

import { useDamage } from '../damages';
import { TireInspection, TireInspections, useTireInspections } from '../tires';

type InspectionSummaryRow = {
  id: number;
  equipmentType?: 'CHASSIS' | 'CONTAINER' | 'GENSET' | 'TIRE';
  location?: string;
  system?: string;
  component?: string;
  damageType?: string;
  notes?: string;
  image?: string;
};

const isTireInspectionsKey = (key: string): key is keyof TireInspections => key in new TireInspections();

const InspectionSummary: FC = () => {
  const { damages, editDamage, deleteDamage } = useDamage();
  const { tires, selectTire } = useTireInspections();

  const editInspection = useCallback(
    (row: InspectionSummaryRow) => {
      if (row.equipmentType === 'TIRE') {
        const key =
          (row.location ?? '').toLowerCase() +
          (row.component ?? '').replace('LEFT', 'Left').replace('RIGHT', 'Right') +
          (row.system ?? '').replace('INNER', 'Inside').replace('OUTER', 'Outside');
        if (isTireInspectionsKey(key)) selectTire(key);
      } else editDamage(row.id);
    },
    [selectTire, editDamage]
  );

  const deleteInspection = useCallback(
    (row: InspectionSummaryRow) => {
      if (row.equipmentType !== 'TIRE') deleteDamage(row.id);
    },
    [deleteDamage]
  );

  const rows: InspectionSummaryRow[] = useMemo(() => {
    const damageRows: InspectionSummaryRow[] = damages.map(
      (damage, id): InspectionSummaryRow => ({
        id,
        equipmentType: damage.equipmentType,
        ...(isChassisDamage(damage) && {
          system: damage.location,
          component: damage.part,
          location: damage.subLocation,
          damageType: damage.damageType,
          notes: damage.notes
        }),
        ...(isContainerDamage(damage) && {
          location: damage.location,
          component: damage.part as string,
          damageType: damage.damageType,
          notes: damage.notes
        }),
        ...(isGensetDamage(damage) && {
          location: damage.location,
          damageType: damage.damageType,
          notes: damage.notes
        }),
        image: damage.imageBase64
      })
    );
    const tireRows: InspectionSummaryRow[] = Object.values(tires)
      .map(t => {
        // console.log(t);
        return t;
      })
      .filter((inspection: TireInspection) => !!inspection.damageType || !!inspection.notes)
      .map((inspection: TireInspection, index) => ({
        id: damages.length + index,
        equipmentType: 'TIRE',
        system: inspection.centrality,
        component: inspection.side,
        location: inspection.axle,
        damageType: inspection.damageType,
        notes: inspection.notes,
        image: inspection.imageBase64
      }));
    return [...damageRows, ...tireRows];
  }, [damages, tires]);

  const getRows = useCallback(async () => {
    // console.log(damages);
    // console.log(rows);
    return rows;
  }, [rows]);

  return (
    <DataTable
      fetchData={getRows}
      columns={[
        {
          field: 'equipmentType',
          label: 'EQUIPMENT',
          width: 120
        },
        { field: 'system', label: 'SYSTEM', width: 96 },
        { field: 'component', label: 'COMPONENT', width: 128 },
        { field: 'location', label: 'LOCATION', width: 104 },
        { field: 'damageType', label: 'DAMAGE', width: 96 },
        { field: 'notes', label: 'NOTES', width: 96, headerClass: 'width-auto' }
      ]}
      initialColumns={['equipmentType', 'system', 'component', 'location', 'damageType', 'notes'].map(c => {
        return { field: c };
      })}
      getRowActions={(row: InspectionSummaryRow) => [
        <div className="d-flex align-items-center g-5">
          <button type="button" className="link" onClick={() => editInspection(row)}>
            <span className="material-icons icon-large">edit</span>
          </button>
          <button
            type="button"
            className={'link' + (row.equipmentType === 'TIRE' ? ' link-disabled' : '')}
            onClick={() => deleteInspection(row)}
            disabled={row.equipmentType === 'TIRE'}
          >
            <span className="material-icons icon-large">delete</span>
          </button>
        </div>
      ]}
      clientSidePagination
      csvFilename="Inspection Summary"
    />
  );
};

export default InspectionSummary;
